import Keycloak from "keycloak-js";

const keycloak = new Keycloak({
  url: process.env.REACT_APP_KEYCLOAK_URL!,
  realm: process.env.REACT_APP_KEYCLOAK_REALM!,
  clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID!,
});

declare global {
  interface Window {
    __gbKeycloakInitPromise?: Promise<void> | null;
    __gbKeycloakInitialized?: boolean;
  }
}

export interface KeycloakProfile {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  emailVerified: boolean;
  attributes: {
    branchName: string[];
    branchCode: string[];
    branchId: string[];
  };
}

export interface KeycloakDecodedToken {
  exp: number;
  iat: number;
  auth_time: number;
  jti: string;
  iss: string;
  aud: string[];
  sub: string;
  typ: string;
  azp: string;
  nonce: string;
  session_state: string;
  acr: string;
  "allowed-origins": string[];
  realm_access: {
    roles: string[];
  };
  resource_access: {
    [client: string]: {
      roles: string[];
    };
  };
  groups: string[];
  scope: string;
  sid: string;
  branch_code: string;
  email_verified: boolean;
  branch_id: string;
  branch_name: string;
  name: string;
  preferred_username: string;
  given_name: string;
  family_name: string;
  email: string;
}

const MIN_VALIDITY = 30;

const formatUnknownError = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === "string") {
    return error;
  }

  if (error && typeof error === "object") {
    const errorWithMessage = error as {
      message?: unknown;
      error?: unknown;
      error_description?: unknown;
    };

    const candidateMessages = [
      errorWithMessage.message,
      errorWithMessage.error_description,
      errorWithMessage.error,
    ];

    for (const candidate of candidateMessages) {
      if (typeof candidate === "string" && candidate.trim()) {
        return candidate;
      }
    }

    try {
      return JSON.stringify(error);
    } catch {
      return "Unknown error";
    }
  }

  return String(error ?? "Unknown error");
};

const buildKeycloakInitError = (error: unknown) => {
  const message = formatUnknownError(error);

  if (message.includes("3rd party check iframe")) {
    return new Error(
      "Authentication initialization failed because the browser blocked the Keycloak session check iframe. Please refresh the page or contact support.",
    );
  }

  return error instanceof Error
    ? error
    : new Error(`Authentication initialization failed: ${message}`);
};

const createInitKeycloakPromise = () =>
  new Promise<void>((resolve, reject) => {
    keycloak
      .init({
        onLoad: "check-sso",
        checkLoginIframe: false,
        silentCheckSsoRedirectUri:
          window.location.origin + "/silent-check-sso.html",
        pkceMethod: "S256",
        flow: "standard",
        responseMode: "fragment",
      })
      .then((authenticated) => {
        if (!authenticated) {
          keycloak.login().catch((error) => {
            window.__gbKeycloakInitPromise = null;
            reject(buildKeycloakInitError(error));
          });
          return;
        }

        if (window.location.search || window.location.hash) {
          window.history.replaceState(
            null,
            "",
            window.location.origin + window.location.pathname,
          );
        }

        keycloak.onTokenExpired = async () => {
          try {
            await keycloak.updateToken(MIN_VALIDITY);
          } catch {
            keycloak.login();
          }
        };

        window.__gbKeycloakInitialized = true;
        resolve();
      })
      .catch((error) => {
        window.__gbKeycloakInitPromise = null;
        reject(buildKeycloakInitError(error));
      });
  });

const initKeycloak = () => {
  if (window.__gbKeycloakInitialized) {
    return Promise.resolve();
  }

  if (!window.__gbKeycloakInitPromise) {
    window.__gbKeycloakInitPromise = createInitKeycloakPromise();
  }

  return window.__gbKeycloakInitPromise;
};

const getToken = () => keycloak.token;

const getDataToken = (): KeycloakDecodedToken | null => {
  const token = keycloak.token;
  if (!token) return null;

  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Failed to decode token:", error);
    return null;
  }
};

const getRoles = () => {
  return getDataToken()?.resource_access?.["gb-client"]?.roles;
};

const getGroups = () => {
  return getDataToken()?.groups;
};

const getBranchCode = (): string | null => {
  const code = getDataToken()?.branch_code;
  return code && code.trim() ? code : null;
};

/** The company id of the user's own branch: where a branch user's new shop is filed. */
const getBranchId = (): string | null => {
  const id = getDataToken()?.branch_id;
  return id ? String(id) : null;
};

const getProfile = () =>
  new Promise<KeycloakProfile>((resolve, reject) => {
    keycloak
      .loadUserProfile()
      .then((profile) => resolve(profile as KeycloakProfile))
      .catch(reject);
  });

const logout = () => {
  keycloak.logout({ redirectUri: window.location.origin });
};

export {
  keycloak,
  initKeycloak,
  getToken,
  getRoles,
  getGroups,
  getBranchCode,
  getBranchId,
  getProfile,
  logout,
};
